import React from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import Signup from "./pages/Signup";
import Login from "./pages/Login";
import Dashboard from "./pages/Dashboard";
import SongLibrary from "./pages/SongLibrary";
import Boards from "./pages/Boards";
import Lists from "./pages/Lists";
import SongLyrics from "./pages/SongLyrics";
import Users from "./pages/Organization/Users";
import Groups from "./pages/Organization/Groups/Groups";
import Events from "./pages/Organization/Events/Events";
import WaitingApproval from "./pages/Organization/WaitingApproval";
import Ministries from "./pages/Organization/Ministry/Ministries";
import MinistryDetail from "./pages/Organization/Ministry/MinistryDetails";
import GroupDetail from "./pages/Organization/Groups/GroupDetail";
import LandingPage from "./pages/LandingPage";
import UserEventDetail from "./pages/User/Eventdetail";
import EventDetail from "./pages/Organization/Events/EventDetail";
import EditSong from "./pages/Organization/Songs/EditSong";
import ViewSong from "./pages/Organization/Songs/ViewSong";
import Cifra from "./pages/Organization/Cifra/Cifra";
import CommunicationsPage from "./pages/Organization/Communications/ComunicationsPage";
import Profile from "./pages/User/Profile";
import CalendarPage from "./pages/Calendar/Calendar";
import SignupOrganization from "./pages/Organization/SignUpOrganization";
import ResetPassword from "./pages/ResetPassword";
import AppLayout from "./components/AppLayout";
import LegacyRedirect from "./utils/LegacyRedirect";
import { RequireAdmin, RequireAuth } from "./utils/RouteGuards";
import { useAuth } from "./context/AuthContext";
import { LoadingPage } from "./components/Loading";

const App: React.FC = () => {
  const { user, loading } = useAuth();

  if (loading) {
    return <LoadingPage />;
  }

  return (
    <Routes>
      <Route path="/" element={user ? <Navigate to="/dashboard" replace /> : <LandingPage />} />

      <Route path="/login" element={<Login />} />
      <Route path="/signup" element={<Signup />} />
      <Route path="/organization-signup" element={<SignupOrganization />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/waiting-approval" element={<WaitingApproval />} />

      <Route element={<RequireAuth />}>
        <Route element={<AppLayout />}>
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/calendar" element={<CalendarPage />} />
          <Route path="/songs" element={<SongLibrary />} />
          <Route path="/songs/:id" element={<ViewSong />} />
          <Route path="/song-search/:artist/:title" element={<SongLyrics />} />
          <Route path="/profile" element={<Profile />} />
          <Route path="/my-events/:eventId" element={<UserEventDetail />} />
          <Route path="/boards" element={<Boards />} />
          <Route path="/boards/:boardId" element={<Lists />} />

          <Route element={<RequireAdmin />}>
            <Route path="/songs/:id/edit" element={<EditSong />} />
            <Route path="/events" element={<Events />} />
            <Route path="/events/:id" element={<EventDetail />} />
            <Route path="/users" element={<Users />} />
            <Route path="/groups" element={<Groups />} />
            <Route path="/groups/:id" element={<GroupDetail />} />
            <Route path="/ministries" element={<Ministries />} />
            <Route path="/ministries/:id" element={<MinistryDetail />} />
            <Route path="/song-importer" element={<Cifra />} />
            <Route path="/communications" element={<CommunicationsPage />} />
          </Route>
        </Route>
      </Route>

      {/* Pre-v2 URLs */}
      <Route path="/login-admin" element={<Navigate to="/login" replace />} />
      <Route path="/dashboard-admin" element={<Navigate to="/dashboard" replace />} />
      <Route path="/user/dashboard" element={<Navigate to="/dashboard" replace />} />
      <Route
        path="/user/eventdetail/:eventId"
        element={<LegacyRedirect to={(p) => `/my-events/${p.eventId}`} />}
      />
      <Route path="/song-database" element={<Navigate to="/songs" replace />} />
      <Route path="/song-search" element={<Navigate to="/songs" replace />} />
      <Route path="/song-viewer" element={<Navigate to="/songs?tab=chords" replace />} />
      <Route
        path="/songs/:artist/:title"
        element={<LegacyRedirect to={(p) => `/song-search/${p.artist}/${p.title}`} />}
      />
      <Route path="/organization/dashboard" element={<Navigate to="/dashboard" replace />} />
      <Route path="/organization/users" element={<Navigate to="/users" replace />} />
      <Route path="/organization/groups" element={<Navigate to="/groups" replace />} />
      <Route
        path="/organization/groups/:id"
        element={<LegacyRedirect to={(p) => `/groups/${p.id}`} />}
      />
      <Route path="/organization/ministries" element={<Navigate to="/ministries" replace />} />
      <Route
        path="/organization/ministries/:id"
        element={<LegacyRedirect to={(p) => `/ministries/${p.id}`} />}
      />
      <Route path="/organization/events" element={<Navigate to="/events" replace />} />
      <Route
        path="/organization/events/:id"
        element={<LegacyRedirect to={(p) => `/events/${p.id}`} />}
      />
      <Route path="/organization/songs" element={<Navigate to="/songs" replace />} />
      <Route
        path="/organization/songs/:id/edit"
        element={<LegacyRedirect to={(p) => `/songs/${p.id}/edit`} />}
      />
      <Route
        path="/organization/songs/:id/view"
        element={<LegacyRedirect to={(p) => `/songs/${p.id}`} />}
      />
      <Route path="/organization/cifra" element={<Navigate to="/song-importer" replace />} />
      <Route
        path="/organization/song-viewer"
        element={<Navigate to="/songs?tab=chords" replace />}
      />
      <Route
        path="/organization/song-search"
        element={<Navigate to="/songs" replace />}
      />
      <Route
        path="/organization/communications"
        element={<Navigate to="/communications" replace />}
      />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

export default App;
